/**
 * SPA root — router + providers (milestone v1.4 / Plan 02-03).
 *
 * Same provider order as v1 (D-30 / D-31):
 *   QueryClientProvider
 *   └─ BrowserRouter (basename=/salescoach)
 *      └─ AuthProvider (JWT-decode-on-load; shares localStorage key with v1
 *         so a single login serves both SPAs)
 *         └─ AppUnauthorizedBridge (navigates to /login on 401)
 *         └─ <Routes>
 *
 * Routes — seven protected tabs inside the layout (IA §1.1 global nav):
 *   /                     → Navigate /overview
 *   /overview             → OverviewPage   (Plan 06 fills widgets)
 *   /engagement           → EngagementPage (Plan 03-06 fills widgets)
 *   /quality              → QualityPage    (Plan 03-07 fills widgets)
 *   /feedback             → FeedbackPage   (Plan 04-07 fills widgets)
 *   /briefs               → BriefsPage     (Plan 04-08 fills widgets)
 *   /operations           → OperationsPage (Plan 03-08 fills widgets)
 *   /user/:username       → UserDrilldownPage (Phase 4 Plan 09)
 *   /login | /login-success — public auth pages ported from v1
 */

import { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AuthProvider, useAuth } from '@/context/AuthContext';
import { queryClient, setUnauthorizedHandler } from '@/lib/query-client';
import { RequireAuth } from '@/components/RequireAuth';
import { UserDetail } from '@/components/UserDetail';
import { DashboardLayout } from '@/pages/DashboardLayout';
import { LoginPage } from '@/pages/LoginPage';
import { LoginSuccessPage } from '@/pages/LoginSuccessPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { OverviewPage } from '@/pages/OverviewPage';
import { EngagementPage } from '@/pages/EngagementPage';
import { JiraPage } from '@/pages/JiraPage';
import { QualityPage } from '@/pages/QualityPage';
import { FeedbackPage } from '@/pages/FeedbackPage';
import { BriefsPage } from '@/pages/BriefsPage';
import { InboxPage } from '@/pages/InboxPage';
import { CampaignsPage } from '@/pages/CampaignsPage';
import { ContactsPage } from '@/pages/ContactsPage';
import { OperationsPage } from '@/pages/OperationsPage';
import { SkillsPage } from '@/pages/SkillsPage';
import { PracticePage } from '@/pages/PracticePage';
import { UserDrilldownPage } from '@/pages/UserDrilldownPage';

/**
 * Registers the 401 handler once both router + auth are mounted. Identical
 * to v1's AppUnauthorizedBridge — future phases may factor this into a
 * shared helper, but for now we keep the two SPAs independent.
 */
function AppUnauthorizedBridge() {
  const { clearAuth } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearAuth();
      navigate('/login', { replace: true });
    });
  }, [clearAuth, navigate]);
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/salescoach">
        <AuthProvider>
          <AppUnauthorizedBridge />
          {/* UserDetail modal — URL-backed (?userDetail=<id>) singleton. Mounted
              once here so any page can trigger open via useOpenUserDetail().
              Lives inside AuthProvider so its data fetches ride the same
              authenticated apiFetch pipeline as the rest of the SPA. */}
          <UserDetail />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login-success" element={<LoginSuccessPage />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <DashboardLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/overview" replace />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="engagement" element={<EngagementPage />} />
              <Route path="jira" element={<JiraPage />} />
              <Route path="quality" element={<QualityPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="briefs" element={<BriefsPage />} />
              <Route path="inbox" element={<InboxPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="practice" element={<PracticePage />} />
              <Route path="operations" element={<OperationsPage />} />
              <Route path="skills" element={<SkillsPage />} />
              <Route path="user/:username" element={<UserDrilldownPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      {import.meta.env.MODE !== 'production' ? (
        <ReactQueryDevtools initialIsOpen={false} />
      ) : null}
    </QueryClientProvider>
  );
}
